/**
 * Money math, shared by a registration sheet (to show a running total) and
 * the server action behind it (to decide what is actually owed). Same
 * function on both sides so the number a person saw is the number they are
 * asked for. Pure: no MP, no framework, safe in the browser.
 *
 * KEEP IN STEP WITH `registrationPricing.ts` in @church/ministry-platform.
 * This is a copy, because the package's entry point drags MP's services in
 * and none of that belongs in a browser bundle — and a copy is exactly how a
 * discount came to be shown against a $0 volunteer on screen and refused on
 * the server. If these two ever need to differ, the difference is a bug.
 */
export interface PriceableOption {
  id: number;
  title: string;
  price: number;
  groupId: number;
}

export interface PriceableProduct {
  basePrice: number;
  depositPrice: number | null;
  options: PriceableOption[];
}

export interface PromoApplied {
  code: string;
  title: string;
  price: number; // negative
  /** Why it applied automatically, if it did. */
  because?: string | null;
}

export interface PricedRegistrant {
  name: string;
  items: { label: string; amount: number }[];
  subtotal: number;
}

export interface PricedRegistration {
  registrants: PricedRegistrant[];
  total: number;
  amountDue: number;
  depositAvailable: boolean;
}

export function priceRegistration(
  product: PriceableProduct | null,
  registrants: { name: string; options: Record<number, number>; promos?: PromoApplied[] }[],
  /** A typed code that applies to everyone, on top of each person's own. */
  promo: PromoApplied | null,
  paymentOption: 'full' | 'deposit'
): PricedRegistration {
  if (!product) {
    return {
      registrants: registrants.map((r) => ({ name: r.name, items: [], subtotal: 0 })),
      total: 0,
      amountDue: 0,
      depositAvailable: false,
    };
  }
  const byId = new Map(product.options.map((o) => [o.id, o]));
  const priced = registrants.map((r) => {
    const items: { label: string; amount: number }[] = [];
    if (product.basePrice > 0) items.push({ label: 'Registration', amount: product.basePrice });
    for (const [idStr, qty] of Object.entries(r.options)) {
      const o = byId.get(Number(idStr));
      if (!o || !qty) continue;
      items.push({ label: qty > 1 ? `${o.title} × ${qty}` : o.title, amount: o.price * qty });
    }
    /**
     * A discount has to have something to come off. Against a seat that never
     * had a price it is a benefit spent on nobody, and "Free" under it is the
     * page claiming to have saved somebody money it never charged. Promos
     * apply only while a price is left, and never take a line below zero.
     */
    let running = round2(items.reduce((s, i) => s + i.amount, 0));
    for (const p of [...(r.promos ?? []), ...(promo ? [promo] : [])]) {
      if (running <= 0) continue;
      const amount = round2(Math.max(p.price, -running));
      items.push({ label: p.title || p.code, amount });
      running = round2(running + amount);
    }
    const subtotal = round2(
      Math.max(
        0,
        items.reduce((s, i) => s + i.amount, 0)
      )
    );
    return { name: r.name, items, subtotal };
  });
  const total = round2(priced.reduce((s, r) => s + r.subtotal, 0));
  const depositAvailable =
    product.depositPrice != null &&
    product.depositPrice > 0 &&
    product.depositPrice * priced.length < total;
  const amountDue =
    paymentOption === 'deposit' && depositAvailable
      ? round2((product.depositPrice as number) * priced.length)
      : total;
  return { registrants: priced, total, amountDue, depositAvailable };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * WHAT IT COSTS, FOR SOMEBODY WHO HAS NOT PICKED YET
 * =================================================
 *
 * The bar at the bottom of a phone has to say a price before anybody has
 * chosen a way in, and `Events.Base_Price` is not that number. Flannel Fest
 * has none at all while a Student pays $10, so reading it alone put "Free" on
 * a page that charges — and next to a button that then said "Register, it is
 * free", which is the same wrong thing twice.
 *
 * The ways in are where the money is. Every one free is Free and says so.
 * Ways in that differ is "From $10", because the cheapest is the only honest
 * single number before somebody has said who they are. One price for
 * everybody is that price.
 *
 * Knowing nothing says nothing. An event whose ways in have not loaded, or
 * that has none, returns null and the bar carries the button alone — which is
 * the one case "Free" must never be, because it is a promise.
 */
export function askingPriceFor(
  ways: readonly { price: number }[],
  basePrice: number | null | undefined,
  money: (n: number) => string
): string | null {
  const prices = ways
    .map((w) => w.price)
    // Negatives are discounts, not ways in, and would drag the floor below
    // anything anybody can actually pay.
    .filter((n) => typeof n === 'number' && !Number.isNaN(n) && n >= 0);

  if (prices.length === 0) {
    if (basePrice == null) return null;
    return basePrice > 0 ? money(basePrice) : 'Free';
  }

  const low = Math.min(...prices);
  const high = Math.max(...prices);
  if (high <= 0) return 'Free';
  if (low === high) return money(high);
  // Flannel Fest: a Student pays $10 and a Volunteer pays nothing. "From $10"
  // overstates it and "Free" undersells it, so both ends are said. Both are
  // true and the reader can see which one is theirs.
  if (low <= 0) return `Free to ${money(high)}`;
  return `From ${money(low)}`;
}

/**
 * THE WAYS IN, SHORT ENOUGH FOR A BAR AT THE BOTTOM OF A PHONE
 * ===========================================================
 *
 * The bar names them rather than only pricing them, because "Volunteer" being
 * one of the answers is how somebody learns that serving is part of this and
 * not a separate thing to go and find. A price alone never says that.
 *
 * WHICH IS ALSO WHY THE OVERFLOW IS NOT JUST THE TAIL. Flannel Fest's ways in
 * are Student, Leader, Student Ministry Staff, Volunteer, in that order, and
 * taking the first three drops the only one that means serving — the exact
 * word the bar is there to show. So if nothing on display takes shifts and
 * something further down does, it takes the last slot.
 */
export function waysInSummary<T extends { label: string; takesShifts: boolean }>(
  ways: readonly T[],
  max = 3
): { shown: string[]; more: number } {
  if (ways.length === 0) return { shown: [], more: 0 };
  if (ways.length <= max) return { shown: ways.map((w) => w.label), more: 0 };

  const head = ways.slice(0, max);
  if (!head.some((w) => w.takesShifts)) {
    const serving = ways.find((w) => w.takesShifts);
    if (serving) head[max - 1] = serving;
  }
  return { shown: head.map((w) => w.label), more: ways.length - max };
}
